<template>
  <div
    class="flex flex-col md:flex-row gap-4"
    :class="stacked ? 'md:flex-col' : 'md:gap-6'"
  >
    <div v-if="!stacked" class="order-last md:order-none">
      <label class="mb-1 inline-flex text-label text-stone-800 md:hidden">
        {{ t('personalization.steps.amount.quantityLabel') }}
      </label>
      <NumberControlButtons
        v-model="quantity"
        class="giftcard-quantity"
        data-test-id="giftcard-quantity"
        size="sm"
        :min="MIN_QUANTITY"
        :max="MAX_QUANTITY"
        :disable-minus="quantity <= MIN_QUANTITY"
        :disable-plus="quantity >= MAX_QUANTITY"
      />
    </div>
    <div class="min-w-0 flex-1">
      <slot name="presets-label" />
      <div
        data-test-id="gifcard-amount-options"
        class="giftcard-amount-picker__options flex justify-between gap-4"
        :class="!stacked && 'md:gap-2'"
      >
        <div
          v-for="amount in presets"
          :key="amount"
          class="chip-amount text-sm font-medium h-[48px] min-h-11 max-w-[120px] py-2 flex-auto text-center justify-center flex items-center cursor-pointer ease duration-300 rounded-3xl border bg-white hover:border-teal-900 hover:text-teal-900 hover:bg-teal-50 text-stone-700"
          :class="[
            isChipSelected(amount)
              ? 'border-teal-600 !bg-teal-50 text-teal-800 !text-primary'
              : 'border-gray-300',
            !stacked && 'md:h-10 md:max-w-none',
          ]"
          @click="selectAmount(amount)"
        >
          {{ formatMoney(amount, locale, undefined, 0) }}
        </div>
      </div>
    </div>

    <div class="min-w-0 flex-1" :class="!stacked && 'md:w-48 md:flex-none'">
      <slot name="input-label" />
      <div class="flex items-center gap-2">
        <div class="relative min-w-0 flex-1">
          <input
            v-model="display"
            type="text"
            inputmode="numeric"
            data-test-id="giftcard-amount-input"
            class="font-regular w-full h-[42px] px-4 border ease duration-300 border-secondary outline-none focus:border-primary-light rounded-lg"
            :class="error.visible ? 'border-red-500' : ''"
            :placeholder="placeholder"
            @focus="handleFocus"
            @blur="handleBlur"
          />
          <div v-if="error.visible" class="text-danger-active text-sm mt-2">
            {{ error.message }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { parsePriceInput, parsePriceToNumber } from '~/utils/priceInputHelpers'

const { t, locale } = useI18n()

const {
  modelValue,
  presets,
  min = 15,
  max = 200,
  placeholder = '',
  stacked = false,
  required = false,
} = defineProps<{
  /** null = nothing chosen yet (no chip highlighted, empty custom field) */
  modelValue: number | null
  presets: number[]
  min?: number
  max?: number
  placeholder?: string
  /** Stack presets above the custom-amount input instead of placing them side by side - for narrow containers (e.g. the sidebar buy box). */
  stacked?: boolean
  /** Treat "no chip and empty custom field" as an error in checkError() instead of "no override". */
  required?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const quantity = defineModel<number>('quantity', { default: 1 })
const MIN_QUANTITY = 1
const MAX_QUANTITY = 1000

// null = custom field shows the placeholder (either untouched, or modelValue
// currently matches a preset chip so there's nothing distinct to show here)
const inputValue = ref<number | null>(null)
const isFocused = ref(false)
const rawInput = ref('')
const error = ref({
  visible: false,
  message: '',
})

const syncRawInput = () => {
  rawInput.value = inputValue.value === null ? '' : String(inputValue.value)
}

watch(
  () => modelValue,
  (value) => {
    inputValue.value = value
    if (!isFocused.value) syncRawInput()
  },
  { immediate: true }
)

// while focused: raw digits, easy to edit. while blurred: "<value> €", matching
// the same pattern as FilterItemPriceRange's price inputs
const display = computed({
  get: () => {
    if (isFocused.value) return rawInput.value
    return inputValue.value === null ? '' : `${inputValue.value} €`
  },
  set: (value: string) => {
    error.value.visible = false

    if (value === '') {
      rawInput.value = ''
      inputValue.value = null
      return
    }

    const cleaned = parsePriceInput(value)
    const numeric = parsePriceToNumber(cleaned)
    rawInput.value = cleaned
    inputValue.value = numeric

    // don't push an out-of-range value to the preview while the user is
    // still typing - it resizes on every keystroke until the amount is
    // valid; checkError() still validates against inputValue regardless
    if (numeric >= min && numeric <= max) emit('update:modelValue', numeric)
  },
})

// modelValue lags behind the input while a typed amount is out of range
// (typing "500" leaves it at the "50" emitted on the way), so only highlight a
// chip when the input isn't showing a different value
const isChipSelected = (amount: number) =>
  modelValue === amount &&
  (inputValue.value === null || inputValue.value === amount)

const selectAmount = (amount: number) => {
  error.value.visible = false
  // set the input directly - if modelValue already equals this amount the
  // watcher won't fire, and the input would keep the stale typed value
  inputValue.value = amount
  syncRawInput()
  emit('update:modelValue', amount)
}

const checkError = (): boolean => {
  // empty custom field just means "no override" - not an invalid amount,
  // unless nothing was chosen at all and an amount is required
  if (inputValue.value === null) {
    const isMissing = required && modelValue === null
    error.value.visible = isMissing
    if (isMissing) error.value.message = t('giftcard.amountRequired')
    return !isMissing
  }

  const isInputValid = inputValue.value >= min && inputValue.value <= max

  if (!isInputValid) {
    error.value.visible = true
    error.value.message = t('giftcard.inputError')
  }

  return isInputValid
}

const handleFocus = (event: FocusEvent) => {
  isFocused.value = true
  syncRawInput()

  const target = event.target as HTMLInputElement
  // move the cursor to the end now that the " €" suffix just disappeared
  setTimeout(
    () =>
      target.setSelectionRange(rawInput.value.length, rawInput.value.length),
    0
  )
}

const handleBlur = () => {
  isFocused.value = false
  checkError()
}

defineExpose({ checkError })
</script>
<style scoped lang="postcss">
.giftcard-quantity {
  @apply w-full h-11 max-w-screen-sm md:max-w-[6em] px-4 md:px-0;
}
</style>
